<template>
    <v-container fill-height fluid pa-0>
        <v-row align="center" class="fill-height" justify="center" v-if="!permission">
            <div class="text-center" style="width: 360px;">
                {{ $t('components.drawer_settings.access_denied') }}
                {{ $t('components.drawer_settings.more_info') }}
                <br>
                <v-btn class="mt-3 mx-2"
                       color="secondary"
                       href="https://support.google.com/chrome/answer/2693767?co=GENIE.Platform%3DDesktop&hl=en&oco=1"
                       target="_blank"
                >{{ $t('components.drawer_settings.chrome') }}
                </v-btn>
                <v-btn class="mt-3 mx-2"
                       color="secondary"
                       href="https://support.mozilla.org/en-US/kb/how-manage-your-camera-and-microphone-permissions"
                       target="_blank"
                >{{ $t('components.drawer_settings.firefox') }}
                </v-btn>
            </div>
        </v-row>
        <v-row align="center" class="px-5" justify="center" no-gutters v-if="permission">
            <v-col cos="12">
                <div class="title">{{ $t('general.settings') }}</div>
            </v-col>
            <v-col class="mb-2" cols="12">
                <div class="text-center">{{ $t('components.drawer_settings.webcam') }}</div>
                <div
                        :style="{ 'border': (streaming ? '2px solid rgba(255,255,255,0.3)' : '2px dashed rgba(255,255,255,0.5)') }"
                        class="mb-3"
                        style="height: 271px; width: 360px; border-radius: 5px;"
                >
                    <transition name="fade-transition">
                        <webcam @gainedWebcamAccess="getSystemDevices" @lostWebcamAccess="getSystemDevices"
                                max-width="356px"
                                webcam-id="drawer-settings"></webcam>
                    </transition>
                </div>
                <v-autocomplete :items="videoInputDevices"
                                :value="selectedVideoInputDevice"
                                @change="setCameraDevice($event)"
                                class="mb-5"
                                hide-details
                                item-color="white"
                                item-text="label"
                                item-value="deviceId"
                                label="Webcam"
                                light
                                no-data-text="No devices found"
                                prepend-inner-icon="mdi-webcam"
                                solo
                ></v-autocomplete>
            </v-col>
            <v-col class="mb-2" cols="12">
                <div class="text-center">
                    {{ $t('components.drawer_settings.microphone') }}
                </div>
                <v-progress-linear :value="audioStreamValue"
                                   color="secondary"
                                   height="10"
                                   rounded
                ></v-progress-linear>
                <v-row justify="space-between" no-gutters>
                    <v-col cols="auto">
                        <small> {{ $t('general.min').toUpperCase() }}</small>
                    </v-col>
                    <v-col cols="auto">
                        <small> {{ $t('general.max').toUpperCase() }}</small>
                    </v-col>
                </v-row>
                <v-autocomplete :items="audioInputDevices"
                                :value="selectedAudioInputDevice"
                                @change="setAudioInputDevice($event)"
                                class="mb-5"
                                hide-details
                                item-color="white"
                                item-text="label"
                                item-value="deviceId"
                                :label="$t('components.checks.mic.mic')"
                                light
                                :no-data-text="$t('components.checks.no_devices')"
                                prepend-inner-icon="mdi-microphone"
                                solo
                ></v-autocomplete>
            </v-col>
            <v-col class="mb-2" cols="12">
                <div class="text-center">{{ $t('components.drawer_settings.audio_out') }}</div>
                <v-progress-linear :value="audioOutputValue"
                                   color="secondary"
                                   height="10"
                                   rounded
                ></v-progress-linear>
                <v-row justify="space-between" no-gutters>
                    <v-col cols="auto">
                        <small> {{ $t('general.min').toUpperCase() }}</small>
                    </v-col>
                    <v-col cols="auto">
                        <small> {{ $t('general.max').toUpperCase() }}</small>
                    </v-col>
                </v-row>
                <v-row class="flex-nowrap" justify="space-between" no-gutters
                       v-if="!!audioOutputDevices && audioOutputDevices.length > 0">
                    <v-autocomplete :items="audioOutputDevices"
                                    :value="selectedAudioOutputDevice"
                                    @change="setAudioOutputDevice($event)"
                                    class="mb-2 mr-2"
                                    hide-details
                                    item-color="white"
                                    item-text="label"
                                    item-value="deviceId"
                                    :label="$t('components.checks.sound.output_device')"
                                    light
                                    :no-data-text="$t('components.checks.no_devices')"
                                    prepend-inner-icon="mdi-headphones"
                                    solo
                    ></v-autocomplete>
                    <v-btn
                           @click="testAudioDevice()"
                           color="secondary"
                           height="48"
                           class="mr-2"
                    >
                        {{ playingOutput ? $t('errors.network.stop') : $t('general.test') }}
                    </v-btn>
                </v-row>
            </v-col>
            <v-btn @click="settingsState()" text>
                <v-icon>mdi-close</v-icon>
                {{ $t('general.close') }}
            </v-btn>
        </v-row>
        <audio>
            <source src="/sounds/audio_test.mp3">
        </audio>
    </v-container>
</template>

<script>
    import Webcam from "../webcam/Webcam";
    import {configureDevices} from "../../lib/mediaDevices";
    import audioInputMixin from "./audioInputMixin";
    import audioOutputMixin from "./audioOutputMixin";

    export default {
        name: "DrawerSettings",
        components: {Webcam},
        props: {
            drawerState: {
                required: true,
                type: Boolean
            }
        },
        mixins: [audioInputMixin, audioOutputMixin],
        data() {
            return {
                videoInputDevices: null,
                audioInputDevices: null,
                audioOutputDevices: null,
                // videoStream: null,audioStream: null,
                testStreamValue: null,
                playingLeft: false,
                playingRight: false,
                streaming: false,
                permission: true,
                playingOutput: false,
                cameraPermissionStatus:null,
                micPermissionStatus:null
            }
        },
        computed: {
            selectedVideoInputDevice() {
                return this.$store.state.media.cameraDevice;
            },
            selectedAudioInputDevice() {
                return this.$store.state.media.audioInputDevice;
            },
            selectedAudioOutputDevice() {
                return this.$store.state.media.audioOutputDevice;
            }
        },
        methods: {
            getSystemDevices() {
                navigator.mediaDevices.enumerateDevices()
                        .then((devices) => {
                                    this.permission = true;
                                    this.videoInputDevices = devices.filter(d => d.kind === 'videoinput');
                                    this.audioInputDevices = devices.filter(d => d.kind === 'audioinput');
                                    this.audioOutputDevices = devices.filter(d => d.kind === 'audiooutput');
                                    configureDevices(true, true, true);
                                },
                                () => {
                                    this.permission = false;

                                });
            },
            clearPermissionListeners(){
                if(this.micPermissionStatus){
                    this.micPermissionStatus.onchange = null;
                    this.micPermissionStatus=null;
                }
                if(this.cameraPermissionStatus){
                    this.cameraPermissionStatus.onchange = null;
                    this.cameraPermissionStatus=null;
                }
            },
            listenForPermission() {
                this.clearPermissionListeners();
                navigator.permissions.query({name: 'camera'}).then((permissionStatus) => {
                    this.cameraPermissionStatus = permissionStatus;
                    this.cameraPermissionStatus.onchange = () => {
                        this.getSystemDevices();
                        this.stopAudioInputStream();
                        this.initAudioContext();
                        this.startAudioInputStream();
                    };
                });
                navigator.permissions.query({name: 'microphone'}).then((permissionStatus) => {
                    this.micPermissionStatus = permissionStatus;
                    this.micPermissionStatus.onchange = () => {
                        this.getSystemDevices();
                        this.stopAudioInputStream();
                        this.initAudioContext();
                        this.startAudioInputStream();
                    };
                });
            },
            testAudioDevice() {
                if(this.playingOutput){
                    this.audioOutputValue=0;
                    this._audio.pause();
                    this.playingOutput = false;
                    return;
                }
                this.playingOutput = true;
                this.audioOutputValue = 0;

                this._audio.play();
                this._audio.onended = () => {
                    setTimeout(() => {
                        this.playingOutput = false;
                    }, 200);
                }
            },
            settingsState() {
                this.$store.dispatch('settings/toggleSettingsDrawer', !this.$store.state.settings.isOpen);
            },
            setCameraDevice(val) {
                this.$store.dispatch('media/setCameraDevice', val);
            },
            setAudioOutputDevice(val) {
                this.$store.dispatch('media/setAudioOutputDevice', val);
            },
            setAudioInputDevice(val) {
                this.$store.dispatch('media/setAudioInputDevice', val);
            },
        },
        mounted() {
            this.listenForPermission();
            this.getSystemDevices();
            this.initAudioContext();
            this.startAudioInputStream();
            this._audio = document.querySelector('audio');

            this.initOutputMonitoring();
            this.monitorOutputAudio();
        },
        beforeDestroy() {
            this.stopAudioInputStream();
            this.stopOutputMonitoring();
            this.clearPermissionListeners();
        }
    }
</script>

<style scoped>
</style>
